/**
 * DashboardMetricsCard Component
 * Enhanced metrics display using Magic UI components
 */

import React from 'react';
import { motion } from 'framer-motion';
import { MagicCard } from '../ui/magic/MagicCard';
import { NumberTicker } from '../ui/magic/NumberTicker';
import { AnimatedCircularProgressBar } from '../ui/magic/AnimatedCircularProgressBar';
import { cn } from '../../lib/utils';

export interface DashboardMetricsCardProps {
  title: string;
  value: number;
  max?: number;
  suffix?: string;
  prefix?: string;
  trend?: {
    direction: 'up' | 'down' | 'neutral';
    value: number;
    suffix?: string;
  };
  icon?: React.ReactNode;
  color?: 'blue' | 'green' | 'amber' | 'purple' | 'pink';
  displayType?: 'number' | 'progress' | 'both';
  className?: string;
}

const colorConfig = {
  blue: {
    bg: 'from-blue-500/10 to-blue-600/5',
    text: 'text-blue-600',
    icon: 'text-blue-500',
    progress: '#3b82f6',
  },
  green: {
    bg: 'from-green-500/10 to-green-600/5',
    text: 'text-green-600', 
    icon: 'text-green-500',
    progress: '#10b981',
  },
  amber: {
    bg: 'from-amber-500/10 to-amber-600/5',
    text: 'text-amber-600',
    icon: 'text-amber-500',
    progress: '#f59e0b',
  },
  purple: {
    bg: 'from-purple-500/10 to-purple-600/5',
    text: 'text-purple-600',
    icon: 'text-purple-500',
    progress: '#8b5cf6',
  },
  pink: {
    bg: 'from-pink-500/10 to-pink-600/5',
    text: 'text-pink-600',
    icon: 'text-pink-500',
    progress: '#ec4899',
  },
};

const trendIcons = {
  up: '↗️',
  down: '↘️',
  neutral: '→',
};

export const DashboardMetricsCard: React.FC<DashboardMetricsCardProps> = ({
  title,
  value,
  max = 100,
  suffix = '',
  prefix = '',
  trend,
  icon,
  color = 'blue',
  displayType = 'number',
  className,
}) => {
  const config = colorConfig[color];

  return (
    <MagicCard 
      className={cn(
        'p-6 transition-all duration-300 hover:scale-[1.02]',
        `bg-gradient-to-br ${config.bg}`,
        className
      )}
      gradientColor={config.progress}
      gradientOpacity={0.15}
    >
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {icon && (
              <div className={cn('p-2 rounded-lg bg-white/50', config.icon)}>
                {icon}
              </div>
            )}
            <h3 className="text-sm font-medium text-gray-600">{title}</h3>
          </div>
          {trend && (
            <motion.div
              className="flex items-center space-x-1 text-xs"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
            >
              <span>{trendIcons[trend.direction]}</span>
              <span className={cn(
                'font-medium',
                trend.direction === 'up' ? 'text-green-600' :
                trend.direction === 'down' ? 'text-red-600' :
                'text-gray-600'
              )}>
                {trend.value}{trend.suffix}
              </span>
            </motion.div>
          )}
        </div>

        {/* Value Display */}
        <div className="flex items-center justify-between">
          {(displayType === 'number' || displayType === 'both') && (
            <div className="flex-1">
              <NumberTicker
                value={value}
                prefix={prefix}
                suffix={suffix}
                className={cn('text-2xl font-bold', config.text)}
                duration={1.5}
                delay={0.2}
              />
            </div>
          )}

          {(displayType === 'progress' || displayType === 'both') && (
            <div className="flex-shrink-0">
              <AnimatedCircularProgressBar
                value={value}
                max={max}
                size={80}
                strokeWidth={6}
                gaugePrimaryColor={config.progress}
                gaugeSecondaryColor="#e5e7eb"
                duration={2}
                delay={0.3}
                showValue={displayType === 'progress'}
                formatValue={(val) => `${Math.round((val / max) * 100)}%`}
              />
            </div>
          )}
        </div>

        {/* Description or additional info */}
        {displayType === 'both' && (
          <div className="text-xs text-gray-500 pt-2 border-t border-gray-200/50">
            {Math.round((value / max) * 100)}% of target ({max}{suffix})
          </div>
        )}
      </div>
    </MagicCard>
  );
};

export default DashboardMetricsCard;